import { jsPDF } from "jspdf";
import { toast } from "react-toastify";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

export const handleDownload = (order: any) => {
  if (!order) {
    alert("No order data available for download.");
    return;
  }

  const doc = new jsPDF();

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const topMargin = 20;
  const bottomMargin = 20;
  const sideMargin = 10;

  const footerStartY = pageHeight - bottomMargin;

  const primaryColor = [10, 41, 117];
  const headerBgColor = [10, 41, 117];

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);

  let currentY = topMargin;

  const image = new Image();
  image.src = "/images/logo.png";

  image.onload = async function () {

    /* ================= FOOTER LINE FUNCTION ================= */
    const drawFooterLine = () => {
      doc.setLineWidth(0.2);
      doc.setDrawColor(0, 0, 0);
      doc.line(
        sideMargin,
        footerStartY - 3,
        pageWidth - sideMargin,
        footerStartY - 3
      );
    };
    /* ======================================================== */

    const aspect = image.naturalWidth / image.naturalHeight;
    const logoHeight = 20;
    const logoWidth = logoHeight * aspect;

    doc.addImage(image, "PNG", sideMargin, topMargin, logoWidth, logoHeight);

    const companyAddressStartY = topMargin + logoHeight + 5;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Quick Engineering Solutions Pvt. Ltd.", sideMargin, companyAddressStartY);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    let txtY = companyAddressStartY + 5;
    doc.text("Naya Nagar, CDF Industrial Area, Anopshahar Road,", sideMargin, txtY);
    txtY += 5;
    doc.text("Cherat, Aligarh, Uttar Pradesh 202122", sideMargin, txtY);
    txtY += 5;
    doc.text("Email: qenggsolutions@gmail.com", sideMargin, txtY);
    txtY += 5;
    doc.text("Phone: +91 8077774614", sideMargin, txtY);

    const leftBottom = txtY;

    let rightY = topMargin;

    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);

    const poTitle = "Purchase Order";
    doc.text(poTitle, pageWidth - sideMargin - doc.getTextWidth(poTitle), rightY);

    rightY += 8;
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);

    const poNumber = `#${order.orderNumber || order.orderId || "NO-PO"}`;
    doc.text(poNumber, pageWidth - sideMargin - doc.getTextWidth(poNumber), rightY);

    currentY = Math.max(leftBottom, rightY) + 15;

    /* ================= CLIENT DETAILS ================= */

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text(order.companyName?.toUpperCase() || "NO COMPANY NAME", sideMargin, currentY);

    currentY += 8;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(order.clientName || "No Client", sideMargin, currentY);

    currentY += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    const addrLines = doc.splitTextToSize(
      String(order.address || "___"),
      pageWidth / 2 - sideMargin - 5
    );

    let leftY = currentY;
    doc.text("Address:", sideMargin, leftY);
    doc.text(addrLines, sideMargin + 18, leftY);
    leftY += addrLines.length * 5;

    doc.text(`Contact No.: ${order.contact || "___"}`, sideMargin, leftY);

    let rightX = sideMargin + (pageWidth - 2 * sideMargin) / 2;
    let rightY2 = currentY;

    doc.text(`Zipcode: ${order.zipCode || "___"}`, rightX, rightY2);
    rightY2 += 5;
    doc.text(`GST No.: ${order.gstNumber || "___"}`, rightX, rightY2);

    currentY = Math.max(leftY, rightY2) + 8;

    /* ================= PRODUCTS TABLE ================= */

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Products", sideMargin, currentY);

    currentY += 5;

    const tableWidth = pageWidth - 2 * sideMargin;
    const colWidths = [15, 65, 25, 85];
    const headers = ["S.No.", "Product", "Quantity", "Remark"];

    const drawTableHeader = () => {
      doc.setFillColor(...headerBgColor);
      doc.rect(sideMargin, currentY, tableWidth, 10, "F");
      doc.setTextColor(255, 255, 255);

      let x = sideMargin;
      headers.forEach((h, i) => {
        doc.text(h, x + 2, currentY + 6);
        x += colWidths[i];
      });

      doc.setTextColor(0, 0, 0);
      currentY += 10;
    };

    drawTableHeader();

    const lineHeight = 5;

    order.products?.forEach((p: any, i: number) => {
      const nameLines = doc.splitTextToSize(String(p.name || "N/A"), colWidths[1] - 4);
      const remarkLines = doc.splitTextToSize(String(p.remark || "N/A"), colWidths[3] - 4);

      const rowHeight = Math.max(
        nameLines.length * lineHeight,
        remarkLines.length * lineHeight,
        10
      );

      if (currentY + rowHeight > footerStartY - 5) {
        drawFooterLine();
        doc.addPage();
        currentY = topMargin;
        drawTableHeader();
      }

      doc.setFillColor(i % 2 === 0 ? 255 : 240);
      doc.rect(sideMargin, currentY, tableWidth, rowHeight, "F");

      doc.text(String(i + 1), sideMargin + 7, currentY + rowHeight / 2, { align: "center" });
      doc.text(nameLines, sideMargin + colWidths[0] + 2, currentY + 4);
      doc.text(
        String(p.quantity || 0),
        sideMargin + colWidths[0] + colWidths[1] + colWidths[2] / 2,
        currentY + rowHeight / 2,
        { align: "center" }
      );
      doc.text(
        remarkLines,
        sideMargin + colWidths[0] + colWidths[1] + colWidths[2] + 2,
        currentY + 4
      );

      currentY += rowHeight;
    });
    currentY += 10;
    // "Generated by" remains as previous (12pt, bold)
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Generated by: ${order.generatedBy?.username || "Rehan"}`, sideMargin, currentY);
    currentY += 7;

    doc.setFont("helvetica", "bold"); // Bold for "Order through"
    doc.text(`Order through: ${order.orderThrough?.username || "Mr. Akif"}`, sideMargin, currentY);
    currentY += 7;
    doc.setFont("helvetica", "bold"); // Bold for "Estimated Date/Time of Dispatch"
    doc.text(`Estimated Date of Dispatch: ${order.estimatedDispatchDate?.split("T")[0] || "2025-07-29"}`, sideMargin, currentY);
    currentY += 10; // Increased space before Note

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Note :", sideMargin, currentY);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    currentY += 7;
    doc.text("1. If you want to update this Purchase Order, You must ask permission from admin/sub-admin", sideMargin, currentY);
    currentY += 5;
    doc.text("2. If you want to delete this Purchase Order, You must ask permission from admin/sub-admin", sideMargin, currentY);

    /* ================= FOOTER CONTENT ================= */

    drawFooterLine();

    const now = new Date();
    const generated = `${now.toLocaleDateString("en-GB")} ${now.toLocaleTimeString("en-GB")} IST`;

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(`Generated on: ${generated}`, sideMargin, footerStartY + 5);

    // -----------------------------------------------------------
    // 🔥 UNIVERSAL DOWNLOAD (WEB + ANDROID APK)
    // -----------------------------------------------------------

    const platform = Capacitor.getPlatform();

    // ✅ FILE NAME USING "_" ONLY
    const safePo = String(order.orderNumber || "NEW_PO").replace(/[^a-zA-Z0-9]/g, "_");
    const fileName = `PO_${safePo}.pdf`;

    if (platform === "web") {
      const blob = doc.output("blob");
      const reader = new FileReader();

      reader.onloadend = function () {
        const a = document.createElement("a");
        a.href = reader.result as string;
        a.download = fileName;
        a.target = "_blank";
        a.click();
      };

      reader.readAsDataURL(blob);
      toast.success("PDF downloaded successfully!");
      return;
    }

    // 📱 ANDROID APK
    const base64 = doc.output("datauristring").split(",")[1];

    const result = await Filesystem.writeFile({
      path: `QESPL/${fileName}`,
      data: base64,
      directory: Directory.Documents,
      recursive: true,
    });

    await Share.share({
      title: fileName,
      text: "Purchase Order",
      url: result.uri,
      dialogTitle: "Share Purchase Order",
    });

    toast.success("PDF saved & ready to share!");
  };

  image.onerror = function () {
    alert("Failed to load logo image for PDF.");
  };
};
